import { StyleSheet, css } from 'aphrodite';
import { subDays } from 'date-fns';
import { observer } from 'mobx-react';
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { TabToggle } from '../../../web/components-v2/TabToggle';
import { SPACE } from '../../../web/components-v2/Theme/theme';
import { bs } from '../../../web/styles/styles';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import {
	useTimeEntryFilterOptionsQuery,
	useTimeEntryFilterQuery,
	useTimeEntryMissingQuery,
	useTimeEntryReviewQueueQuery,
} from '../../queries/TimeEntry';
import { todayYmd } from '../../timeTracking/days';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { useIsMgrRole } from '../../timeTracking/useIsMgrRole';
import { useTimeTracking } from '../../timeTracking/useTimeTracking';
import { useUserNames } from '../../timeTracking/useUserNames';
import { AllEntriesTable } from './AllEntriesTable';
import { MissingRepsTable } from './MissingRepsTable';
import { RepDaysDrawer } from './RepDaysDrawer';
import { ReviewQueueTable } from './ReviewQueueTable';
import {
	ALL,
	buildEntriesFilter,
	buildQueueOptionsFilter,
	defaultManagerFilter,
	distinctOffices,
	matchesReviewFilters,
} from './reviewFilters';

const RECENT_DAYS = 30;
const VIEWS = [
	{ label: 'Review queue', value: 'queue' },
	{ label: 'All entries', value: 'all' },
	{ label: 'Missing', value: 'missing' },
] as const;
type View = (typeof VIEWS)[number]['value'];

const viewParser = parseAsStringEnum<View>(VIEWS.map(item => item.value)).withDefault('queue');
const filterParser = parseAsString.withDefault(ALL);
const repParser = parseAsString.withOptions({ history: 'push' });
// No default: absent means "the caller's default", which depends on their role.
const managerParser = parseAsString;

function _HoursReview() {
	const userSession = useUserSession();
	const { enabled, showHoursReview, canActAsSelf, isAdmin } = useTimeTracking();
	const { isLoading: isLoadingPermissions } = useManagerPermissions();
	const { isLoading: isLoadingRole, isMgr } = useIsMgrRole({ enabled: enabled && isAdmin });
	const { nameFor, initialsFor } = useUserNames({ enabled });
	const myId = userSession.user?.id;

	const [view, setView] = useQueryState('view', viewParser);
	const [rep, setRep] = useQueryState('rep', filterParser);
	const [office, setOffice] = useQueryState('office', filterParser);
	const [managerParam, setManagerParam] = useQueryState('manager', managerParser);
	const [openRep, setOpenRep] = useQueryState('repDays', repParser);

	// An admin's queue and All entries are account-wide and paged, so their manager pick is filtered on the API; a non-admin is
	// already scoped to their reports. The URL keeps a manager pick only when it differs from the default.
	const filtersOnApi = isAdmin && view === 'all';
	const defaultManager = defaultManagerFilter({ isManager: isMgr, userId: myId });
	const manager = managerParam ?? defaultManager;
	const managerFilter = isAdmin ? manager : ALL;
	// The API ignores the queue's manager under impersonation, where the caller isn't the admin acting as themselves.
	const queueFiltersOnApi = isAdmin && canActAsSelf;
	const showManagerFilter = view === 'all' ? isAdmin : queueFiltersOnApi;
	const setManager = (value: string) => setManagerParam(value === defaultManager ? null : value);

	const queueQuery = useTimeEntryReviewQueueQuery({
		enabled: enabled && !isLoadingRole,
		managerUserId: queueFiltersOnApi && managerFilter !== ALL ? managerFilter : undefined,
		userId: rep === ALL ? undefined : rep,
	});
	const fromDay = useMemo(() => todayYmd(subDays(new Date(), RECENT_DAYS)), []);
	const allFilter = useMemo(
		() => buildEntriesFilter({ fromDay, manager: managerFilter, rep: isAdmin ? rep : ALL }),
		[fromDay, isAdmin, managerFilter, rep]
	);
	const allQuery = useTimeEntryFilterQuery({ enabled: enabled && view === 'all' && !isLoadingRole, filter: allFilter });
	const missingQuery = useTimeEntryMissingQuery({
		enabled: enabled && view === 'missing' && !isLoadingRole,
		managerUserId: queueFiltersOnApi && managerFilter !== ALL ? managerFilter : undefined,
	});
	const optionsFilter = useMemo(
		() =>
			view === 'queue'
				? buildQueueOptionsFilter({ manager: queueFiltersOnApi ? managerFilter : ALL })
				: buildEntriesFilter({ fromDay, manager: view === 'all' ? managerFilter : ALL, rep: ALL }),
		[view, fromDay, queueFiltersOnApi, managerFilter]
	);
	const optionsQuery = useTimeEntryFilterOptionsQuery({
		enabled: enabled && view !== 'missing' && !isLoadingRole,
		filter: optionsFilter,
	});

	const reviews = queueQuery.data?.values ?? [];
	const allEntries = allQuery.data?.values ?? [];
	const visibleReviews = reviews.filter(review => matchesReviewFilters(review.entry, { office, rep: ALL }));
	const visibleEntries = allEntries.filter(entry =>
		matchesReviewFilters(entry, { office, rep: filtersOnApi ? ALL : rep })
	);
	const toOptions = (userIds: string[]) =>
		userIds.map(userId => ({ label: nameFor(userId), value: userId })).sort((a, b) => a.label.localeCompare(b.label));
	const optionEntries = view === 'queue' ? reviews.map(review => review.entry) : allEntries;
	// The queue never shows the caller's own days, though the options count their submitted ones.
	const repOptions = toOptions(
		(optionsQuery.data?.userIds ?? []).filter(userId => view !== 'queue' || userId !== myId)
	);
	const showRepFilter = view !== 'missing' && (isAdmin || repOptions.length > 1 || rep !== ALL);
	const managerUserIds =
		(view === 'missing' ? missingQuery.data?.managerUserIds : optionsQuery.data?.managerUserIds) ?? [];
	const showMyReports = !!myId && (manager === myId || managerUserIds.includes(myId));
	const otherManagerOptions = toOptions(managerUserIds.filter(userId => userId !== myId));
	const managerLabel = manager === ALL ? 'All managers' : manager === myId ? 'My reports' : nameFor(manager);
	const officeOptions = distinctOffices(optionEntries);
	const showOfficeFilter = view !== 'missing' && (officeOptions.length > 0 || office !== ALL);

	if (!enabled) {
		return <Redirect to='/' />;
	}
	if (isLoadingPermissions || (!showHoursReview && queueQuery.isLoading)) {
		return <LoadingSpinner type='large' />;
	}
	if (!showHoursReview) {
		return <Redirect to='/timesheet' />;
	}

	return (
		<div className={css(tt.page)}>
			<h1 className={css(tt.pageTitle)}>Hours review</h1>
			<div className={css(styles.toolbar)}>
				<TabToggle
					items={[...VIEWS]}
					value={view}
					onValueChange={value => value && setView(value as View)}
					aria-label='Hours review view'
				/>
				{showManagerFilter ? (
					<div className={css(styles.select)}>
						<Select value={manager} onValueChange={(value: string | null) => value && setManager(value)}>
							<SelectTrigger styles={[bs.bgWhite, bs.wFull]} aria-label='Manager'>
								<SelectValue>{managerLabel}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL}>All managers</SelectItem>
								{showMyReports ? <SelectItem value={myId}>My reports</SelectItem> : null}
								{otherManagerOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}
				{showRepFilter ? (
					<div className={css(styles.select)}>
						<Select value={rep} onValueChange={(value: string | null) => value && setRep(value === ALL ? null : value)}>
							<SelectTrigger styles={[bs.bgWhite, bs.wFull]} aria-label='Rep'>
								<SelectValue>{rep === ALL ? 'All reps' : nameFor(rep)}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL}>All reps</SelectItem>
								{repOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}
				{showOfficeFilter ? (
					<div className={css(styles.select)}>
						<Select
							value={office}
							// Base UI reports null when the value briefly isn't among the items (options reloading); only a pick changes it.
							onValueChange={(value: string | null) => value && setOffice(value === ALL ? null : value)}
						>
							<SelectTrigger styles={[bs.bgWhite, bs.wFull]}>
								<SelectValue>{office === ALL ? 'All offices' : office}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL}>All offices</SelectItem>
								{officeOptions.map(option => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}
				{view === 'all' ? <span className={css(tt.muted, styles.summary)}>Last {RECENT_DAYS} days</span> : null}
			</div>

			{view === 'queue' ? (
				<ReviewQueueTable
					reviews={visibleReviews}
					isLoading={isLoadingRole || queueQuery.isLoading}
					isError={queueQuery.isError}
					onRetry={() => queueQuery.refetch()}
					canActAsSelf={canActAsSelf}
					nameFor={nameFor}
					initialsFor={initialsFor}
					onOpenRep={setOpenRep}
				/>
			) : view === 'missing' ? (
				<MissingRepsTable
					reps={missingQuery.data?.reps ?? []}
					isManagerPicked={queueFiltersOnApi && managerFilter !== ALL}
					isLoading={isLoadingRole || missingQuery.isLoading || missingQuery.isPlaceholderData}
					isError={missingQuery.isError}
					onRetry={() => missingQuery.refetch()}
					nameFor={nameFor}
					initialsFor={initialsFor}
					onOpenRep={setOpenRep}
				/>
			) : (
				<AllEntriesTable
					entries={visibleEntries}
					isLoading={isLoadingRole || allQuery.isLoading}
					isError={allQuery.isError}
					onRetry={() => allQuery.refetch()}
					nameFor={nameFor}
					initialsFor={initialsFor}
					onOpenRep={setOpenRep}
				/>
			)}

			<RepDaysDrawer
				key={openRep ?? 'closed'}
				userId={openRep}
				isAdmin={isAdmin}
				canActAsSelf={canActAsSelf}
				nameFor={nameFor}
				onClose={() => setOpenRep(null)}
			/>
		</div>
	);
}

export const HoursReview = observer(_HoursReview);

const styles = StyleSheet.create({
	select: {
		width: 200,
	},
	summary: {
		marginLeft: 'auto',
	},
	toolbar: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[12],
	},
});
